<script setup>
import { Icon } from '@components'
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch } from 'vue'

const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    inputClass: {
        type: String,
    },
    inputStyle: {
        type: String,
    },
    icon: {
        type: [String, Boolean],
        default: 'edit05'
    },
    iconVisibility: {
        type: Boolean,
        default: true
    },
    blur: {
        type: Boolean,
    },
    border: {
        type: Boolean,
        default: true
    },
    editable: {
        type: Boolean,
        default: true
    },
    submitButton: {
        type: Boolean,
        default: false
    }
})

const state = reactive({
    hover: false,
    editable: false,
    showIcon: false,
    inputChanged: false
})

const emit = defineEmits(['update:modelValue', 'focus', 'blur', 'submit', 'cancel'])

const input = ref(null)
const container = ref(null)

const focus = (type = 'button') => {
    if (!props.editable) return

    emit('focus')
    input.value.setAttribute('data-text', input.value.innerText)
    state.editable = true
    state.showIcon = true

    input.value.focus()

    if (type === 'edit') {
        setCursorToEnd(input.value)
    }
}

const editable = computed(() => props.editable)

const blur = () => {
    if (!props.editable) return
    emit('blur')
    state.editable = false
    state.showIcon = false
}

const submit = () => {
    if (!props.editable) return
    emit('update:modelValue', input.value.innerText)
    input.value.dataset.text = input.value.innerText
    emit('submit')
    blur()
    state.inputChanged = false
}

const handleClickOutside = (event) => {
    if (container.value && !container.value.contains(event.target)) {
        blur()
    }
}

const setCursorToEnd = (element) => {
    const range = document.createRange()
    const sel = window.getSelection()
    range.selectNodeContents(element)
    range.collapse(false)
    sel.removeAllRanges()
    sel.addRange(range)
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside)
})

</script>

<template>
    <div ref="container" v-bind="$attrs" class="inline-flex items-center prebook-editable w-fit editable relative" @click="focus()" @mouseenter="state.hover = true" @mouseleave="state.hover = false" :class="{ 'animate-purple pointer-events-none': props.blur }">
        <div ref="input" class="outline-none prebook-editable-input w-fit cursor-text editable-input pr-[22px]" @input="state.inputChanged = true" :contenteditable="editable" :tabindex="state.editable ? 0 : -1" @keyup="emit('update:modelValue', $event.target.innerText)" :class="{ 'border border-[#B692F6] p-1': border }" :style="props.inputStyle" v-html="props.modelValue"></div>

       <transition name="fade">
         <Icon 
            v-if="state.inputChanged && props.submitButton"
            name="check" 
            class="text-[#7f56d9] border border-[#7f56d9] hover:bg-[#a797c8] hover:text-white rounded p-1 absolute right-0 top-1/2 -translate-y-1/2" 
            @click.stop="submit()" 
        />
        <Icon 
            v-else-if="state.hover && props.iconVisibility && props.editable && !state.inputChanged"
            :name="props.icon" 
            class="text-slate-500 absolute right-0 top-1/2 -translate-y-1/2" 
            @click.stop="focus('edit')" 
        />
        </transition>
        
    </div>
</template>
